import { styled } from "@mui/material/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React from "react";
import { SortDirectionEnum } from "../Premises/types";
import { TasksTableRow, SortByEnum } from "./types";
import { mapTasksTableRowKeyToSortByEnum } from "./helpers";
import { Box, Spinner, Text } from "@origin-digital/ods-core";

import { TaskStatusIcon } from "./Icons";
import { ConnectionTypeIconWithHoverTip } from "../Icons";
import { normaliseIdColumnValue, updateToDiscard } from "../Premises/helpers";
import { useTheme } from "src/hooks";
import { useTasksNavigation } from "./hooks";
import { useFeedContext } from "src/context/Feed";
import { TasksContextMenu } from "./TasksContextMenu";
import { DeleteDraftDialogProvider } from "../Drafts";

const StyledTableCell = styled(TableCell)(() => ({
  textAlign: "left",
  verticalAlign: "middle",
}));

const StyledTableHeadCell = styled(StyledTableCell)(() => ({
  backgroundColor: "#f8f8f8",
}));

const StyledTableRow = styled(TableRow)(() => ({
  cursor: "pointer",
  height: 64,
}));

type ColumnConfig = {
  id: keyof TasksTableRow;
  label: string;
  width?: string;
  flex?: number;
  disableSorting?: boolean;
};

const columns: ColumnConfig[] = [
  { id: "connectionType", label: "Type", width: "30px" },
  { id: "address", label: "Address", flex: 1 },
  { id: "id", label: "ID", width: "80px" },
  { id: "taskAdded", label: "Task added", width: "100px" },
  { id: "taskStatusText", label: "Task status", flex: 1 },
];

export const TasksTable: React.FC = () => {
  const {
    rows,
    sortConfig,
    setSortConfig,
    doFetch,
    setPage,
    loading,
    setScrollPosition,
    scrollableProps,
  } = useFeedContext();

  const theme = useTheme();
  const handleSort = (key: keyof TasksTableRow) => {
    const isAsc =
      sortConfig &&
      sortConfig.key === mapTasksTableRowKeyToSortByEnum(key) &&
      sortConfig.direction === SortDirectionEnum.ASC;
    const sortBy =
      mapTasksTableRowKeyToSortByEnum(key) ?? SortByEnum.TASK_ADDED;
    const sortDirection = isAsc
      ? SortDirectionEnum.DESC
      : SortDirectionEnum.ASC;
    setSortConfig({
      key: sortBy,
      direction: sortDirection,
    });
    setPage(0);
    setScrollPosition(0);
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortBy,
        direction: sortDirection,
      },
    });
  };

  const reFetchTasks = () => {
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortConfig?.key,
        direction: sortConfig?.direction,
      },
    });
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        maxHeight: 700,
        maxWidth: 1440, // Set maximum width
        overflow: "auto",
        margin: "0 auto", // Center the table container
      }}
      onScroll={scrollableProps.onContainerScroll}
      ref={scrollableProps.containerRef}
    >
      <Table
        sx={{ minWidth: 650, tableLayout: "fixed" }}
        size="small"
        aria-label="tasks table"
        stickyHeader
      >
        <TableHead sx={{ backgroundColor: "#F1F1F1" }}>
          <StyledTableRow>
            {columns.map((column) => {
              const isCurrentCol =
                sortConfig?.key === mapTasksTableRowKeyToSortByEnum(column.id);
              return (
                <StyledTableHeadCell
                  key={column.id}
                  sx={{
                    width: column.width,
                    flex: column.flex,
                    minWidth: column.flex ? "100px" : undefined,
                  }}
                >
                  {column.disableSorting ? (
                    <Text variant="body-small" weight="medium">
                      {column.label}
                    </Text>
                  ) : (
                    <TableSortLabel
                      active={isCurrentCol}
                      direction={
                        isCurrentCol
                          ? (sortConfig.direction.toLowerCase() as
                              | "desc"
                              | "asc")
                          : "asc"
                      }
                      onClick={() => handleSort(column.id)}
                      sx={{
                        "&.Mui-active .MuiTableSortLabel-icon": {
                          color: theme.colors.critical,
                        },
                      }}
                    >
                      <Text
                        variant="body-small"
                        tone={isCurrentCol ? "critical" : undefined}
                        weight="medium"
                      >
                        {column.label}
                      </Text>
                    </TableSortLabel>
                  )}
                </StyledTableHeadCell>
              );
            })}
            <StyledTableHeadCell align="right" sx={{ width: "40px" }} />
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows &&
            rows.map((row, index) => (
              <EnhancedRow
                key={index}
                row={row}
                reFetchTasks={reFetchTasks}
                tableColumns={columns}
              />
            ))}
        </TableBody>
      </Table>
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY="medium"
        >
          <Spinner />
        </Box>
      )}
    </TableContainer>
  );
};

const Row = ({
  row,
  reFetchTasks,
  tableColumns,
}: {
  row: TasksTableRow;
  reFetchTasks: () => void;
  tableColumns: ColumnConfig[];
}) => {
  const { handleOnClick } = useTasksNavigation(row);
  const idColumnDisplayValue = normaliseIdColumnValue(
    row.id,
    row.journeyId,
    row.distributorApplicationId
  );

  const onDeleteDraftConfirmed = () => {
    updateToDiscard(row.id, reFetchTasks);
  };

  return (
    <>
      <StyledTableRow onClick={handleOnClick}>
        {tableColumns.map((column) => (
          <StyledTableCell
            key={column.id}
            component="th"
            scope="row"
            sx={{
              width: column.width,
              flex: column.flex,
              minWidth: column.flex ? "100px" : undefined,
            }}
            onClick={(e) => column.id === "id" && e.stopPropagation()}
          >
            {column.id === "connectionType" && (
              <ConnectionTypeIconWithHoverTip
                type={row.connectionType}
                state={row.state}
              />
            )}
            {column.id === "address" && (
              <Text variant="body-small">{row.address}</Text>
            )}
            {column.id === "id" && (
              <Text variant="body-small">{idColumnDisplayValue}</Text>
            )}
            {column.id === "taskAdded" && (
              <Text variant="body-small">{row.taskAdded || "-"}</Text>
            )}
            {column.id === "taskStatusText" && (
              <Box display="flex" alignItems="center">
                <Box marginTop="xxsmall" marginRight="xsmall">
                  <TaskStatusIcon taskStatus={row.taskStatus} />
                </Box>
                <Text
                  tone={row.taskStatus === "error" ? "critical" : "neutral"}
                  variant="body-small"
                >
                  {row.taskStatusText}
                </Text>
              </Box>
            )}
          </StyledTableCell>
        ))}
        <StyledTableCell padding="normal" sx={{ width: "40px" }}>
          <TasksContextMenu
            onDeleteDraftConfirmed={onDeleteDraftConfirmed}
            row={row}
          />
        </StyledTableCell>
      </StyledTableRow>
    </>
  );
};

const EnhancedRow = (props: React.ComponentProps<typeof Row>) => (
  <DeleteDraftDialogProvider>
    <Row {...props} />
  </DeleteDraftDialogProvider>
);
